// NEW_FILE_JUSTIFIED: Hook encapsulating AI document extraction for claim wizard — triggers extractClaimData and maps results to treatment_items entries

import { useState, useCallback } from 'react';
import { useAction } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need Convex generated API
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need Convex generated dataModel
import { toast } from 'sonner';

// LOCAL_TYPE_JUSTIFIED: Hook return type is internal to this hook
interface IUseClaimDocumentAnalysisReturn {
  analyzeDocuments: (storageIds: string[]) => void;
  isAnalyzing: boolean;
}

export function useClaimDocumentAnalysis(
  updateValue: (key: string, value: unknown) => void,
  currentValues: Record<string, unknown>
): IUseClaimDocumentAnalysisReturn {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const extractClaimData = useAction(api.insurance.extractClaimDocument.extractClaimData);

  const analyzeDocuments = useCallback(
    (storageIds: string[]) => {
      if (storageIds.length === 0) return;

      setIsAnalyzing(true);

      void (async () => {
        try {
          const result = await extractClaimData({
            storageIds: storageIds as Id<'_storage'>[], // AS_JUSTIFIED: storageIds from Convex upload response, safe to narrow
          });

          if (!result.success || !result.data) {
            toast.error(
              'Could not extract data from the uploaded documents. Please enter treatment details manually.'
            );
            return;
          }

          const { data } = result;

          // Map extracted lineItems to treatment_items — fill empty items first, then add new ones
          if (data.lineItems && data.lineItems.length > 0) {
            const existingItems = Array.isArray(currentValues['treatment_items'])
              ? (currentValues['treatment_items'] as Array<{
                  id: string;
                  values: Record<string, unknown>;
                }>) // AS_JUSTIFIED: treatment_items shape set by RepeatableGroupField
              : [];

            const aiEntries = data.lineItems.map(item => ({
              dateOfService: item.dateOfService ?? '',
              description: item.description ?? '',
              cost: item.amount ?? 0,
              currency: item.currency ?? 'EUR',
            }));

            // Separate existing items into empty (unfilled) and filled
            const isItemEmpty = (v: Record<string, unknown>) => !v['description'] && !v['cost'];
            const emptyItems = existingItems.filter(item => isItemEmpty(item.values));
            const filledItems = existingItems.filter(item => !isItemEmpty(item.values));

            // Fill empty items first with AI data
            const updatedEmpty = emptyItems.map((item, i) => {
              if (i < aiEntries.length) {
                return { ...item, values: { ...item.values, ...aiEntries[i] } };
              }
              return item;
            });

            // Create new entries for any remaining AI items beyond what empty slots absorbed
            const remainingAiEntries = aiEntries.slice(emptyItems.length).map(vals => ({
              id: crypto.randomUUID(),
              values: vals,
            }));

            updateValue('treatment_items', [
              ...filledItems,
              ...updatedEmpty,
              ...remainingAiEntries,
            ]);
            toast.success(
              `Extracted ${aiEntries.length} treatment item${aiEntries.length > 1 ? 's' : ''} from documents`
            );
          }

          // Prefill provider fields if currently empty
          if (data.providerName && !currentValues['provider_name']) {
            updateValue('provider_name', data.providerName);
          }
          if (data.providerAddress && !currentValues['provider_address']) {
            updateValue('provider_address', data.providerAddress);
          }

          // Prefill patient name (split "First Last") if both name fields are empty
          if (
            data.patientName &&
            !currentValues['patient_firstName'] &&
            !currentValues['patient_lastName']
          ) {
            const nameParts = data.patientName.trim().split(/\s+/);
            if (nameParts.length === 1) {
              // Single-token name — whole value is the first name; no surname to set
              if (nameParts[0]) updateValue('patient_firstName', nameParts[0]);
            } else {
              const lastName = nameParts.pop() ?? '';
              const firstName = nameParts.join(' ');
              if (firstName) updateValue('patient_firstName', firstName);
              if (lastName) updateValue('patient_lastName', lastName);
            }
          }

          // Prefill claim detail fields when empty
          if (data.dateOfService && !currentValues['claim_dateOfIllness']) {
            updateValue('claim_dateOfIllness', data.dateOfService);
          }
          if (data.diagnosis && !currentValues['claim_diagnosis']) {
            updateValue('claim_diagnosis', data.diagnosis);
          }
          if (data.symptoms && !currentValues['claim_symptoms']) {
            updateValue('claim_symptoms', data.symptoms);
          }

          // Prefill total claimed amount when empty
          if (
            data.totalAmount !== undefined &&
            currentValues['payment_totalClaimed'] === undefined
          ) {
            updateValue('payment_totalClaimed', data.totalAmount);
          }
        } catch {
          toast.error('Failed to analyze documents. Please enter treatment details manually.');
        } finally {
          setIsAnalyzing(false);
        }
      })();
    },
    [extractClaimData, updateValue, currentValues]
  );

  return { analyzeDocuments, isAnalyzing };
}
